import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Paginator } from "primereact/paginator";
import { apigetallCurrentaffairs } from "../../../admin/shared/services/apiCurrentaffairs/apicurrentaffairs";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import { Spinner, Tooltip } from "@nextui-org/react";
import { useSearchParams } from "react-router-dom";
import { apigetHeaderEnabledAffairs } from "../../services/apiCurrentAffairs/apicurrentaffairs";
import { X } from "lucide-react";

export default function CurrentAffairsHome() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [globalFilter, setGlobalFilter] = useState('');
    const [searchValue, setSearchValue] = useState("");
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [categoryCounts, setCategoryCounts] = useState({});
    const [searchParams, setSearchParams] = useSearchParams();
    const affairType = searchParams.get('type');
    const [selectedAffairType, setSelectedAffairType] = useState("");

    useEffect(() => {
        if (affairType) {
            setSelectedAffairType(affairType);
        } else {
            setSelectedAffairType("");
        }
    }, [affairType]);
    
    const handleInputChange = (e) => {
        const value = e.target.value;
        setSearchValue(value);
        setGlobalFilter(value);
        setFirst(0);
    };

    const handleClear = () => {
        setSearchValue("");
        setGlobalFilter("");
    };

    const getVideoCourses = useCallback(async () => {
        setLoading(true);
        try {
            let res;
            
            if (selectedAffairType) {
                res = await apigetHeaderEnabledAffairs({affairType: selectedAffairType,first,rows,globalFilter,category: selectedCategory});
            } else {
                res = await apigetallCurrentaffairs({first, rows, globalFilter,category: selectedCategory});
            }
            
            setTableData(res?.data || res?.resdata || []); 
            setTotalRecords(res?.totalRecords || res?.totallength || 0);
            
            if (!selectedCategory && (res?.data || res?.resdata)) {
                const dataArray = res?.data || res?.resdata;
                const categoryMap = {};
                dataArray.forEach(item => {
                    if (item.Category) {
                        categoryMap[item.Category] = (categoryMap[item.Category] || 0) + 1;
                    }
                });
                setCategoryCounts(categoryMap);
                setCategories(Object.keys(categoryMap));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setTableData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [first, rows, globalFilter, selectedCategory, selectedAffairType]);
    
    const fetchAllCategories = useCallback(async () => {
        try {
            const res = await apigetallCurrentaffairs({ getCategories: true });
            if (res?.categories) {
                setCategories(res.categories);
                setCategoryCounts(res.categoryCounts || {});
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    }, []);

    useEffect(() => {
        getVideoCourses();
    }, [first, rows, globalFilter, selectedCategory, selectedAffairType]);

    useEffect(() => {
        if (categories.length === 0) {
            fetchAllCategories();
        }
    }, []);

    const handleCategoryClick = (category) => {
        if (selectedCategory === category) {
            setSelectedCategory("");
        } else {
            setSelectedCategory(category);
        }
        setFirst(0);
    };

    const handleClearAffairType = () => {
        setSelectedAffairType("");
        navigate('/current-affairs', { replace: true });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    useEffect(() => {
        const handleContextMenu = (e) => {
            e.preventDefault();
        };
    
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
                e.preventDefault();
            }
        };
    
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('keydown', handleKeyDown);
    
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);
    

    return (
        <>
        <div className="max-w-[85rem] mx-auto py-8 px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
                <div className="col-span-9 p-4">
                    <h2 className="mb-6 text-3xl font-bold text-gray-800">Current Affairs</h2>
                    
                    {selectedCategory && (
                        <div className="mb-4">
                            <span className="inline-flex items-center px-3 py-1 text-sm bg-blue-100 rounded-full">
                                <span className="mr-2 font-medium text-blue-800">{selectedCategory}</span>
                                <button onClick={() => setSelectedCategory("")} className="text-blue-700 hover:text-blue-900">
                                   <X/>
                                </button>
                            </span>
                        </div>
                    )}

                    <div className="relative mb-5">
                        <div className="absolute inset-y-0 z-20 flex items-center pointer-events-none start-0 ps-4">
                            <svg className="flex-shrink-0 text-gray-800 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></svg>
                        </div>
                        <input onChange={handleInputChange} value={searchValue}  type="text"  id="icon"  name="icon"
                            className="block w-full px-4 py-3 text-sm border-[#1f2937] outline-none border-1 rounded-xl ps-11 pe-10 disabled:opacity-50 disabled:pointer-events-none"
                            placeholder="Search current affairs"
                        />

                        {searchValue && (
                            <div className="absolute inset-y-0 right-0 z-20 flex items-center pr-3 cursor-pointer" onClick={handleClear}>
                                <svg className="flex-shrink-0 text-gray-800 size-4 hover:text-gray-600" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 6 6 18" />
                                    <path d="m6 6 12 12" />
                                </svg>
                            </div>
                        )}
                    </div>
                    
                    {loading ? (
                        <div className="flex items-center justify-center h-40">
                            <Spinner size="lg" color="primary" className="text-blue-500" />
                        </div>
                    ) : tableData.length === 0 ? (
                        <div className="p-4 text-center bg-gray-100 rounded-lg">
                            <p>No current affairs available</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {tableData.map((item) => (
                                <div key={item._id} className="p-4 border rounded-lg shadow-sm">
                                    <div className="items-start justify-between lg:flex">
                                        <div>
                                            <h3 className="text-xl font-semibold text-blue-700">{item.Title}</h3>
                                            
                                            <div className="mt-3 text-sm text-gray-500">
                                                <span className="mr-4">{formatDate(item.createdAt)}</span>
                                                <span className="px-2 py-1 text-blue-800 bg-blue-100 rounded-md cursor-pointer hover:bg-blue-200" onClick={() => handleCategoryClick(item.Category)}>
                                                    {item.Category}
                                                </span>
                                                <span className="px-2 py-1 ml-2 text-green-800 bg-green-100 rounded-md">{item.Current_Affair_Type}</span>
                                            </div>
                                        </div>
                                        <div className="mt-5">
                                            <Link to={`/current-affairs-detail/${item._id}`} className="px-4 py-2 text-sm font-medium text-white rounded-md bg-regal-blue hover:bg-blue-700">
                                                Read More
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {totalRecords > 0 && (
                        <div className="mt-6">
                            <Paginator first={first} rows={rows} totalRecords={totalRecords} rowsPerPageOptions={[5, 10, 20]} onPageChange={onPageChange}
                                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                            />
                        </div>
                    )}
                </div>

                <div className="w-full col-span-3 mx-14 lg:mx-0 lg:mt-10 ">
                    <div className="sticky p-4 border rounded-lg shadow-sm h-fit top-20">
                        <h3 className="mb-4 text-lg font-semibold text-blue-700">Categories</h3>
                        <ul className="space-y-2">
                            {categories.length > 0 ? (
                                categories.map((category) => (
                                    <li key={category}
                                        className={`flex items-center justify-between p-2 border rounded-md cursor-pointer ${selectedCategory === category ? 'bg-blue-100 border-blue-300' : 'hover:bg-blue-100'}`}
                                        onClick={() => handleCategoryClick(category)}
                                    >
                                        <span className={selectedCategory === category ? 'font-medium' : ''}>{category}</span>
                                        <span className="text-sm text-gray-500">{categoryCounts[category] || 0}</span>
                                    </li>
                                ))
                            ) : (
                                <li className="p-2 text-gray-500">Loading categories...</li>
                            )}
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <a href="https://wa.me/9047127777?text=Hello%20I%20want%20to%20enquire%20about%20your%20services" target="_blank" rel="noopener noreferrer">
            <button>
                <Tooltip color="success" content="Contact on Whatsapp Now!" placement="left-start" showArrow={true} >
                    <img src="/images/whatsapp.png" alt="" className="fixed z-50 bottom-24 right-5 w-14 h-14 animate-bounce-up-down " />
                </Tooltip>
            </button>
        </a>

        <button id="to-top-button" title="Go To Top"
            className="fixed z-50 hidden p-4 text-lg font-semibold text-white transition-colors duration-300 border-0 rounded-full shadow-md bottom-5 right-5 w-14 h-14 bg-regal-blue hover:bg-regal-yellow"
        >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6" >
                <path d="M12 4l8 8h-6v8h-4v-8H4l8-8z" />
            </svg>
        </button>
        </>
    );
}